import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  FormHelperText,
  Grid2
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import InputField from '../components/common/FormFields/InputField';
import SelectField from '../components/common/FormFields/SelectField';
import { knowledgeValidationSchema } from '../validations/knowledgeValidation';

// Define interfaces for the API data
interface Category {
  _id: string;
  name: string;
  description: string;
}

interface KnowledgeCreateInput {
  title: string;
  description: string;
  duration: number;
  category: string;
  videoUrl1: string;
  videoUrl2?: string;
  status: 'DRAFT' | 'PUBLISHED';
  isActive: boolean;
}

const LearningAdd: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [duration, setDuration] = useState<number>(30);
  const [categoryId, setCategoryId] = useState<string>('');
  const [videoUrl1, setVideoUrl1] = useState<string>('');
  const [videoUrl2, setVideoUrl2] = useState<string>('');
  const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Call the correct API endpoint for categories
        const response = await api.get('/knowledge-categories');
        setCategories(response.data.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to mock category in case of error
        setCategories([
          {
            _id: "67d68f0d8a8c3ab66edada39",
            name: "ความปลอดภัย",
            description: "หมวดหมู่สำหรับบทเรียนด้านความปลอดภัย"
          }
        ]);
      }
    };
    
    fetchCategories();
  }, []);
  
  const handleBack = () => {
    navigate('/learning');
  };
  
  const validateField = async (field: string, value: any) => {
    try {
      await knowledgeValidationSchema.validateAt(field, { [field]: value });
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    } catch (error: any) {
      setFormErrors(prev => ({ ...prev, [field]: error.message }));
    }
  };

  const handleFieldChange = async (field: string, value: any) => {
    // Update state
    switch (field) {
      case 'title':
        setTitle(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'duration':
        setDuration(Number(value));
        break;
      case 'category':
        setCategoryId(value);
        break;
      case 'videoUrl1':
        setVideoUrl1(value);
        break;
      case 'videoUrl2':
        setVideoUrl2(value);
        break;
      case 'status':
        setStatus(value as 'DRAFT' | 'PUBLISHED');
        break;
      case 'isActive':
        setIsActive(value === 'true');
        break;
    }
    
    // Validate field
    await validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate all fields
      const formData = {
        title,
        description,
        duration,
        category: categoryId,
        videoUrl1,
        videoUrl2,
        status,
        isActive
      };
      
      await knowledgeValidationSchema.validate(formData, { abortEarly: false });
      
      setLoading(true);
      
      // Prepare data for API
      const knowledgeData: KnowledgeCreateInput = {
        ...formData,
        videoUrl2: videoUrl2 || undefined
      };
      
      // Call the actual API endpoint to create knowledge
      await api.post('/knowledge', knowledgeData);
      
      setSnackbar({
        open: true,
        message: 'เพิ่มบทเรียนสำเร็จ',
        severity: 'success',
      });
      
      // Wait for snackbar to show before navigating
      setTimeout(() => {
        navigate('/learning');
      }, 1500);
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        // Handle validation errors
        const errors: Record<string, string> = {};
        error.inner.forEach((err: any) => {
          errors[err.path] = err.message;
        });
        setFormErrors(errors);
      } else {
        console.error('Error adding knowledge:', error);
        setSnackbar({
          open: true,
          message: 'เกิดข้อผิดพลาดในการเพิ่มบทเรียน',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/learning');
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };
  
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          เพิ่มบทเรียนออนไลน์
        </Typography>
      </Box>
      
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>

        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="h6" gutterBottom>
              ข้อมูลบทเรียน
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <InputField
              label="ชื่อบทเรียน"
              value={title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              required
              name="title"
              error={!!formErrors.title}
              helperText={formErrors.title}
            />
          </Grid2>
          <Grid2 size={12}>
            <InputField
              label="คำอธิบายบทเรียน"
              multiline
              rows={4}
              value={description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              name="description"
              error={!!formErrors.description}
              helperText={formErrors.description}
            />
          </Grid2>
          <Grid2 size={12}>
            <SelectField
              label="หมวดหมู่"
              value={categoryId}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              required
              name="category"
              options={categories.map((category) => ({
                value: category._id,
                label: category.name
              }))}
              error={!!formErrors.category}
              helperText={formErrors.category}
            />
          </Grid2>
          <Grid2 size={12}>
            <InputField
              label="ระยะเวลา (นาที)"
              type="number"
              value={duration.toString()}
              onChange={(e) => handleFieldChange('duration', e.target.value)}
              required
              name="duration"
              error={!!formErrors.duration}
              helperText={formErrors.duration}
            />
          </Grid2>
          <Grid2 size={12}>
            <InputField
              label="ลิงก์วิดีโอหลัก"
              value={videoUrl1}
              onChange={(e) => handleFieldChange('videoUrl1', e.target.value)}
              required
              name="videoUrl1"
              error={!!formErrors.videoUrl1}
              helperText={formErrors.videoUrl1}
            />
          </Grid2>
          <Grid2 size={12}>
            <InputField
              label="ลิงก์วิดีโอเสริม (ถ้ามี)"
              value={videoUrl2}
              onChange={(e) => handleFieldChange('videoUrl2', e.target.value)}
              name="videoUrl2"
              error={!!formErrors.videoUrl2}
              helperText={formErrors.videoUrl2}
            />
          </Grid2>
          <Grid2 size={12}>
            <SelectField
              label="สถานะ"
              value={status}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              required
              name="status"
              options={[
                { value: 'DRAFT', label: 'แบบร่าง' },
                { value: 'PUBLISHED', label: 'เผยแพร่' }
              ]}
              error={!!formErrors.status}
              helperText={formErrors.status}
            />
          </Grid2>
          <Grid2 size={12}>
            <SelectField
              label="การใช้งาน"
              value={isActive ? "true" : "false"}
              onChange={(e) => handleFieldChange('isActive', e.target.value)}
              required
              name="isActive"
              options={[
                { value: 'true', label: 'เปิดการใช้งาน' },
                { value: 'false', label: 'ปิดการใช้งาน' }
              ]}
              error={!!formErrors.isActive}
              helperText={formErrors.isActive}
            />
          </Grid2>
          
        </Grid2>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LearningAdd; 